/**
 * Explicit ECharts module registration + the theme bridge (mirrors
 * `apps/web/src/charts/echarts-setup.ts`).
 *
 * `vue-echarts` renders with the tree-shakeable `echarts/core` build, which has
 * NO renderers/charts/components registered by default — each must be `use()`d
 * once, process-wide. This module performs that registration as an import
 * side-effect; `main.ts` imports it once at bootstrap. Every chart SFC still
 * imports `VChart from 'vue-echarts'` LOCALLY (there is no global component).
 */
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
]);

/** HSL (h deg, s%, l%) → 0-255 RGB channels. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sat = s / 100;
  const lum = l / 100;
  const chroma = (1 - Math.abs(2 * lum - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((hp % 2) - 1));
  const [r, g, b] = (
    [
      [chroma, x, 0],
      [x, chroma, 0],
      [0, chroma, x],
      [0, x, chroma],
      [x, 0, chroma],
      [chroma, 0, x],
    ] as const
  )[Math.floor(hp) % 6];
  const m = lum - chroma / 2;
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

/**
 * Resolve a theme token to a concrete color ECharts can paint with.
 *
 * The design tokens are HSL *channel* triplets (`--chart-1: 160 84% 39%`), which only become
 * colors inside `hsl(...)`. We must NOT hand ECharts the modern space-separated
 * `hsl(160 84% 39% / 0.25)` form: canvas paints it (the BROWSER parses it), but ECharts derives
 * hover/emphasis colors with zrender's OWN parser, which only understands the legacy comma
 * syntax + rgb/rgba/hex. On a parse miss the emphasis fill resolves to nothing and the hovered
 * bar / line renders INVISIBLE. Emitting `rgba(...)` keeps both painters happy.
 */
export function token(name: string, alpha = 1): string {
  const channels = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!channels) return 'transparent';
  const [h, s, l] = channels.split(/\s+/).map((part) => Number.parseFloat(part));
  if ([h, s, l].some(Number.isNaN)) return 'transparent';
  const [r, g, b] = hslToRgb(h, s, l);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
